import type { CSSProperties, ReactNode } from "react";
import { tokens } from "../../design-system/tokens";

export type ChipColor = "green" | "orange" | "gray";
export type ChipSize = "small" | "medium" | "large" | "xl";

export interface ChipProps {
  children: ReactNode;
  color?: ChipColor;
  size?: ChipSize;
  width?: string | number;
  maxWidth?: string | number;
  className?: string;
  style?: CSSProperties;
}

export default function Chip({
  children,
  color = "gray",
  size = "medium",
  width,
  maxWidth,
  className,
  style,
}: ChipProps) {
  const height =
    size === "xl" ? "50px" : size === "large" ? "40px" : size === "medium" ? "36px" : "25px";
  const borderRadius =
    size === "small"
      ? tokens.borderRadius.full
      : size === "medium"
        ? tokens.borderRadius.full
        : tokens.borderRadius.full;
  const padding =
    size === "xl"
      ? "10px 14px"
      : size === "large"
        ? "8px 12px"
        : size === "medium"
          ? "6px 10px"
          : "5px 12px";
  const typography =
    size === "small"
      ? tokens.typography.buttonSmall
      : size === "xl"
        ? tokens.typography.buttonXl
        : tokens.typography.button;

  const resolvedLineHeight = size === "small" ? 1 : typography.lineHeight;

  const backgroundColor =
    color === "green"
      ? tokens.colors.chipGreen
      : color === "orange"
        ? tokens.colors.chipOrange
        : tokens.colors.chipGray;

  return (
    <span
      className={className}
      style={{
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...(width !== undefined
          ? { width, minWidth: 0 }
          : { minWidth: "fit-content" }),
        ...(maxWidth !== undefined ? { maxWidth } : {}),
        height,
        padding,
        borderRadius,
        border: "1px solid transparent",
        backgroundColor,
        color: tokens.colors.text.primary,
        fontFamily: typography.fontFamily,
        fontWeight: typography.fontWeight,
        fontSize: typography.fontSize,
        lineHeight: String(resolvedLineHeight),
        letterSpacing: typography.letterSpacing,
        textAlign: "center",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span style={{ display: "inline-block", transform: "translateY(1px)" }}>{children}</span>
    </span>
  );
}
